import { action, computed, makeObservable, observable, reaction, IReactionDisposer } from "mobx";
import React, { Suspense } from "react";

import SplashScreen from "components/SplashScreen/SplashScreen";

import ComponentRouter from "./ComponentRouter";
import injectStores from "../RootStore/injectStores";
// import { LayoutType } from '../../types/LayoutType';
import { AuthStore, RouterStore, ViewStore, ConnectionStore } from "stores";

interface LayoutRouterProps {
  routerStore?: RouterStore;
  splashImageName?: string;
  viewStore?: ViewStore;
  authStore?: AuthStore;
  connectionStore?: ConnectionStore;
}

class LayoutRouter extends React.PureComponent<LayoutRouterProps> {
  loadingMessage: string = "Initializing...";
  loadingStartTime: number = Date.now();
  messageTimeouts: NodeJS.Timeout[] = [];
  disposers: IReactionDisposer[] = [];
  authAttempts: number = 0;
  
  constructor(props: LayoutRouterProps) {
    super(props);
    makeObservable(this, {
      loadingMessage: observable,
      setLoadingMessage: action,
    });
    
    this.setupLoadingMessageUpdates();
  }

  componentWillUnmount() {
    // Clean up reactions and timeouts
    this.disposers.forEach(dispose => dispose());
    this.messageTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  setLoadingMessage = (message: string): void => {
    this.loadingMessage = message;
  };

  setupLoadingMessageUpdates = () => {
    const { authStore, routerStore, connectionStore } = this.props;
    
    // Monitor initialization states and update messages accordingly
    const messageReaction = reaction(
      () => ({
        isConnected: connectionStore?.serverConnectionStatus,
        isAuthPending: authStore?.isAuthenticatePending,
        isSignedIn: authStore?.isSignedIn,
        authError: authStore?.errorOnAuthenticate,
        routesInitialized: routerStore?.routesInitialized,
        currentRoute: routerStore?.currentRoute,
        authAttempts: this.authAttempts,
      }),
      (state) => {
        this.updateLoadingMessage(state);
      },
      { fireImmediately: true }
    );
    
    this.disposers.push(messageReaction);
    
    // Set up timeout messages for slow operations
    this.setupTimeoutMessages();
  };

  updateLoadingMessage = (state: any) => {
    const elapsedSeconds = Math.floor((Date.now() - this.loadingStartTime) / 1000);
    
    // Priority order for messages
    if (state.authError) {
      // Authentication error occurred
      const errorMessage = state.authError.message || state.authError.toString();
      if (errorMessage.includes("401") || errorMessage.includes("Unauthorized")) {
        this.setLoadingMessage("Authentication failed. Please check your credentials.");
      } else if (errorMessage.includes("Network") || errorMessage.includes("fetch")) {
        this.setLoadingMessage("Network error. Please check your connection.");
      } else {
        this.setLoadingMessage("Authentication error. Please try again.");
      }
      return;
    }
    
    if (!state.isConnected && state.isConnected !== undefined) {
      // Not connected to server
      if (elapsedSeconds > 10) {
        this.setLoadingMessage("Having trouble connecting to the server...");
      } else {
        this.setLoadingMessage("Connecting to server...");
      }
      return;
    }
    
    if (state.isAuthPending) {
      // Authentication in progress
      if (elapsedSeconds > 15) {
        this.setLoadingMessage("Authentication is taking longer than expected...");
      } else if (elapsedSeconds > 5) {
        this.setLoadingMessage("Verifying your credentials...");
      } else {
        this.setLoadingMessage("Logging you in...");
      }
      return;
    }
    
    if (!state.routesInitialized) {
      // Routes not yet initialized
      if (elapsedSeconds > 10) {
        this.setLoadingMessage("Loading application resources...");
      } else if (elapsedSeconds > 3) {
        this.setLoadingMessage("Preparing your workspace...");
      } else {
        this.setLoadingMessage("Checking for updates...");
      }
      return;
    }
    
    if (!state.currentRoute && state.routesInitialized) {
      // Routes initialized but no current route
      this.setLoadingMessage("Navigating to your dashboard...");
      return;
    }
    
    // Everything seems ready
    if (state.isSignedIn) {
      this.setLoadingMessage("Welcome back! Loading your workspace...");
    } else {
      this.setLoadingMessage("Preparing application...");
    }
  };

  setupTimeoutMessages = () => {
    // Show additional messages if things are taking too long
    const timeout1 = setTimeout(() => {
      const { authStore } = this.props;
      if (!authStore?.isSignedIn && authStore?.isAuthenticatePending) {
        this.setLoadingMessage("Still working on authentication... Please wait.");
      }
    }, 20000); // 20 seconds
    
    const timeout2 = setTimeout(() => {
      const { authStore, connectionStore } = this.props;
      if (!authStore?.isSignedIn && !connectionStore?.serverConnectionStatus) {
        this.setLoadingMessage("This is taking unusually long. Please check your internet connection.");
      }
    }, 30000); // 30 seconds
    
    const timeout3 = setTimeout(() => {
      const { routerStore } = this.props;
      if (!routerStore?.currentRoute) {
        this.setLoadingMessage("Still loading... If this persists, please refresh the page.");
      }
    }, 45000); // 45 seconds
    
    this.messageTimeouts.push(timeout1, timeout2, timeout3);
  };

  render() {
    const { viewStore, routerStore } = this.props;

    const currentLayout = computed(() => routerStore.currentLayout).get();
    if (!currentLayout) {
      return <SplashScreen message={this.loadingMessage} />;
    }
    // return <div>Render</div>;
    const LayoutComponent = currentLayout.component;

    const isWebView = viewStore.isWebView;
    // return (
    //   <LayoutComponent>
    //     <div>Rendersss</div>
    //   </LayoutComponent>
    // );

    return (
      <Suspense fallback={<SplashScreen message={this.loadingMessage} />}>
        {!!LayoutComponent && !isWebView ? (
          <LayoutComponent>
            <div style={{ paddingRight: 8, paddingLeft: 8 }}>
              <ComponentRouter />
            </div>
          </LayoutComponent>
        ) : (
          <ComponentRouter />
        )}
      </Suspense>
    );
  }
}

export default injectStores(LayoutRouter);
